<script>
import UserDetailStatisticTile from '@/components/user/tiles/UserDetailStatisticTile.vue';
import UserDetailBarChartTile from '@/components/user/tiles/UserDetailBarChartTile.vue';
import UserDetailTableTile from '@/components/user/tiles/UserDetailTableTile.vue';
import utils from '@/utils/common';

// уточнить по поводу баланса рефералов и последней транзакции
// не хватает информации по статусам и прибыли

const priceFormatter = new Intl.NumberFormat('ru', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 });
const formatDate = (date) => utils.convertDateToString(date, 'short');

export default {
  name: 'UserDetailReferralsTab',
  components: { UserDetailStatisticTile, UserDetailBarChartTile, UserDetailTableTile },
  props: {
    referrals: Array,
    payouts: Array,
    user: Object,
  },
  computed: {
    affiliateBalance() {
      return priceFormatter.format(Math.round(this.user.affiliateBalance / 100));
    },

    referralsPayments() {
      return this.referrals
        .reduce((acc, val) => [...acc, ...val.referral.payments], [])
        .filter(p => p.status === 'confirmed')
        .map(p => ({ ...p, time: new Date(p.createdAt).getTime() }));
    },

    paymentsSum() {
      return this.referralsPayments.reduce((acc, val) => acc + val.amount, 0);
    },

    paymentsSumCurrentMonth() {
      const month = new Date().getMonth();
      const year = new Date().getFullYear();

      return this.referralsPayments
        .filter(p => {
          const date = new Date(p.createdAt);
          return date.getMonth() === month && date.getFullYear() === year;
        })
        .reduce((acc, val) => acc + val.amount, 0);
    },

    paymentsThirtyDaysBefore() {
      const actualDate = new Date();
      const actualTime = actualDate.getTime();

      const oldestDate = new Date(actualTime);
      oldestDate.setDate(actualDate.getDate() - 30);
      const oldestTime = oldestDate.getTime();

      return this.referralsPayments
        .filter(p => p.time <= actualTime && p.time >= oldestTime)
        .reduce((acc, val) => acc + val.amount, 0);
    },

    fullIncome() {
      return priceFormatter.format(Math.floor(this.paymentsSum * 0.3) / 100);
    },

    monthIncome() {
      return priceFormatter.format(Math.floor(this.paymentsSumCurrentMonth * 0.3) / 100);
    },

    thirtyDaysIncome() {
      return priceFormatter.format(Math.floor(this.paymentsThirtyDaysBefore * 0.3) / 100);
    },

    referralsStatistic() {
      const summary = this.referrals.length;
      const active = (this.referrals ?? []).filter(r => (r.referral.accountConfirmed && !r.referral.accountBlocked)).length;
      const unActive = (this.referrals ?? []).filter(r => (!r.referral.accountConfirmed || r.referral.accountBlocked)).length;
      const lastPayout = [...this.payouts].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).pop();
      const lastPayoutValue = lastPayout ? Math.round(lastPayout.amount / 100) : 0;

      return {
        summary:          { label: 'Кол-во рефералов',       icon: 'chart-orange',    value: summary,            title: ''  }, // eslint-disable-line
        active:           { label: 'Активные рефералы',      icon: 'check-green',     value: active,             title: ''  }, // eslint-disable-line
        unActive:         { label: 'Неактивные рефералы',    icon: 'close-red',       value: unActive,           title: ''  }, // eslint-disable-line
        lastTransaction:  { label: 'Последняя транзакция',   icon: 'cash-blue',       value: lastPayoutValue,    title: '₽' }, // eslint-disable-line
      };
    },

    lastPayout() {
      const lastPayout = [...this.payouts].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).pop();
      const targetUserName = (lastPayout?.targetUser.name && lastPayout?.targetUser.surname) ? `${lastPayout?.targetUser.name} ${lastPayout?.targetUser.surname}` : lastPayout?.targetUser.email;
      const payoutType = (lastPayout?.targetUser['@id'] === this.user['@id']) ? 'на свой аккаунт' : `на аккаунт ${targetUserName}`;

      return lastPayout
        ? [
        { title: 'Дата',        value: formatDate(new Date(lastPayout?.createdAt))                 }, // eslint-disable-line
        { title: 'Сумма',       value: priceFormatter.format(Math.round(lastPayout?.amount / 100)) }, // eslint-disable-line
        { title: '№ платежа',   value: lastPayout?.id                                              }, // eslint-disable-line
        { title: 'Тип',         value: payoutType                                                 }, // eslint-disable-line
        { title: 'Статус',      value: 'выполнен'                                                 }, // eslint-disable-line
        ]
        : null;
    },

    payoutBarColors() {
      return [
        'rgb(51, 161, 251)',
      ];
    },

    months() {
      return [
        { name: 'Январь',     value: 0  }, // eslint-disable-line
        { name: 'Феварль',    value: 1  }, // eslint-disable-line
        { name: 'Март',       value: 2  }, // eslint-disable-line
        { name: 'Апрель',     value: 3  }, // eslint-disable-line
        { name: 'Май',        value: 4  }, // eslint-disable-line
        { name: 'Июнь',       value: 5  }, // eslint-disable-line
        { name: 'Июль',       value: 6  }, // eslint-disable-line
        { name: 'Август',     value: 7  }, // eslint-disable-line
        { name: 'Сентябрь',   value: 8  }, // eslint-disable-line
        { name: 'Октябрь',    value: 9  }, // eslint-disable-line
        { name: 'Ноябрь',     value: 10 }, // eslint-disable-line
        { name: 'Декабрь',    value: 11 }, // eslint-disable-line
      ];
    },

    payoutBarData() {
      const payoutsByMonth = [];
      const labels = [];

      const startDate = new Date();
      const startMonth = startDate.getMonth();

      for (let i = startMonth; i > startMonth - 12; i -= 1) {
        const newDate = new Date(startDate.getTime());
        newDate.setMonth(i);
        const month = newDate.getMonth();
        const year = newDate.getFullYear();

        const monthConfig = this.months.find(m => m.value === newDate.getMonth());
        labels.push(`${monthConfig?.name} ${year}`);

        const payouts = (this.payouts ?? [])
          .filter(p => {
            const payoutDate = new Date(p.createdAt);
            return payoutDate.getMonth() === month && payoutDate.getFullYear() === year;
          }).reduce((sum, payout) => ({
            ...sum,
            amount: sum?.amount + payout?.amount,
          }), { amount: 0 });

        payoutsByMonth.push(Math.round(payouts.amount / 100));
      }

      return { labels, payoutsByMonth };
    },

    tasksBarConfig() {
      return {
        backgroundColors: this.payoutBarColors,
        labels: this.payoutBarData.labels,
        barThickness: 55,
        borderWidth: 0,
        borderRadius: 5,
        stepSize: Math.round(Math.max(...this.payoutBarData.payoutsByMonth) / 3),
      };
    },

    referralsHeaders() {
      return [
        { title: 'Пользователь',    width: 25,    component: 'AdminUserListTileCell',   props: (item) => ({ row: item }),                               sortFn: (a, b) => a.nameSortIndex.localeCompare(b.nameSortIndex), required: true }, // eslint-disable-line
        { title: 'Регистрация',     width: 15,    component: 'BaseTableCell',           props: (item) => ({ value: item.createdAtFormatted }),          sortFn: (a, b) => a.createdAt - b.createdAt                                                }, // eslint-disable-line
        { title: 'Платежи',         width: 10,    component: 'BaseTableCell',           props: (item) => ({ value: item.paymentsSumFormatted, }),       sortFn: (a, b) => a.paymentsSum - b.paymentsSum                                  }, // eslint-disable-line
        { title: 'Прибыль',         width: 10,    component: 'BaseTableCell',           props: (item) => ({ value: item.profitFormatted, isBold: true }),            sortFn: (a, b) => a.profit - b.profit                                            }, // eslint-disable-line
        { title: 'Статус',          width: 10,    component: 'BaseTableCell',           props: (item) => ({ value: item.status, color: this.setStatusCellColor(item?.status), isBold: true }),                                                                                                      }, // eslint-disable-line
      ];
    },

    referralsItems() {
      return (this.referrals ?? []).map(r => {
        const paymentsConfirmedSum = r?.referral.payments
          .filter(p => p.status === 'confirmed')
          .reduce((sum, payment) => ({
            ...sum,
            amount: sum?.amount + payment?.amount,
          }), { amount: 0 });

        const status = (r?.referral.accountBlocked && !r?.referral.accountConfirmed)
          ? 'не активен'
          : 'активен';

        return {
          ...r?.referral,
          createdAtFormatted: formatDate(new Date(r?.referral.createdAt)),
          createdAt: new Date(r?.referral.createdAt).getTime(),
          nameSortIndex: (r?.referral.name && r?.referral.surname) ? r?.referral.name + r?.referral.surname : r?.referral.email,
          paymentsSum: paymentsConfirmedSum?.amount,
          profit: paymentsConfirmedSum?.amount * 0.3,
          profitFormatted: priceFormatter.format(Math.floor((paymentsConfirmedSum?.amount * 0.3) / 100)),
          paymentsSumFormatted: priceFormatter.format(Math.round(paymentsConfirmedSum?.amount / 100)),
          roles: [],
          status,
        };
      });
    },

    payoutsHistoryHeaders() {
      return [
        { title: '№ платежа',   width: 10,    component: 'BaseTableCell',   props: (item) => ({ value: item?.number }),                required: true }, // eslint-disable-line
        { title: 'Дата',        width: 10,    component: 'BaseTableCell',   props: (item) => ({ value: item?.createdAtFormatted }),               }, // eslint-disable-line
        { title: 'Тип',         width: 13,    component: 'BaseTableCell',   props: (item) => ({ value: item?.type, }),                            }, // eslint-disable-line
        { title: 'Сумма',       width: 7,     component: 'BaseTableCell',   props: (item) => ({ value: item?.amount, isBold: true, }),                         }, // eslint-disable-line
        { title: 'Статус',      width: 12,    component: 'BaseTableCell',   props: (item) => ({ value: item?.status, color: this.setStatusCellColor(item?.status), isBold: true }),                          }, // eslint-disable-line
      ];
    },

    payoutsHistoryItems() {
      return (this.payouts ?? []).map((p, i) => {
        const targetUserName = (p?.targetUser.name && p?.targetUser.surname) ? `${p?.targetUser.name} ${p?.targetUser.surname}` : p?.targetUser.email;
        const payoutType = (p?.targetUser['@id'] === this.user['@id']) ? 'на свой аккаунт' : `на аккаунт ${targetUserName}`;

        return {
          number: p?.id,
          id: new Date(p?.createdAt).getTime() + i,
          createdAtFormatted: formatDate(new Date(p?.createdAt)),
          createdAt: p?.createdAt,
          type: payoutType,
          amount: priceFormatter.format(Math.round(p?.amount / 100)),
          status: 'выполнен',
        };
      });
    },
  },
  methods: {
    setStatusCellColor(status) {
      if (status === 'активен' || status === 'выполнен') {
        return 'rgb(90, 193, 105)';
      }

      if (status === 'не активен') {
        return 'rgb(241, 114, 117)';
      }

      return '';
    },
  },
};
</script>

<template lang="pug">
.b-user-detail-referrals-tab
  UserDetailStatisticTile._statistic(v-if="referrals" :statistic="referralsStatistic" :statisticTileWidth="206" label="Баланс")
    template(v-slot:header)
      ._statistic-finances
        ._finances-available
          ._finances-value {{ affiliateBalance }}
          ._finances-title Доступно
        ._finances-full
          ._finances-value {{ fullIncome }}
          ._finances-title Заработано за всё время
        ._finances-month
          ._finances-value {{ monthIncome }}
          ._finances-title Заработано в этом месяце
        ._finances-calendar-month
          ._finances-value {{ thirtyDaysIncome }}
          ._finances-title Заработано за последние 30 дней
  ._last-payment-block(v-if="payouts")
    ._last-payment-header Последняя выплата
    ._last-payment-data
      template(v-if="lastPayout")
        ._last-payment-row(v-for="row in lastPayout" :key="row.title")
          ._last-payment-title {{ row.title }}
          ._last-payment-value {{ row.value }}
      ._last-payment-empty-info(v-else) Нет выплат
  UserDetailBarChartTile._referrals-payout(:data="payoutBarData.payoutsByMonth" :config="tasksBarConfig" commonLabel="Начисления по партнёрской программе, по месяцам")
  UserDetailTableTile._referrals-table(:headers="referralsHeaders" :items="referralsItems" label="Рефералы")
  UserDetailTableTile._payouts-table(:headers="payoutsHistoryHeaders" :items="payoutsHistoryItems" label="История выплат")
</template>

<style scoped lang="scss">
.user-detail-referrals-tab {
  display: grid;
  grid-template-columns: 1fr 312px;
  //grid-auto-rows: auto;
  gap: 38px;

  &__statistic {
    grid-column: 1;
    grid-row: 1 / 2 span;
  }

  &__statistic-finances {
    border-radius: 8px;
    background-color: rgb(248, 249, 252);
    //box-sizing: border-box;
    padding: 20px 30px;
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
  }

  &__finances-available, &__finances-full, &__finances-month, &__finances-calendar-month {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 250px;
  }

  &__finances-full {
    border-left: 1px solid rgb(146, 160, 176);
  }

  &__finances-full, &__finances-month  {
    border-right: 1px solid rgb(146, 160, 176);
  }

  &__finances-value {
    color: rgb(51, 161, 251);
    font-family: Geometria, sans-serif;
    font-size: 30px;
    font-weight: 700;
    line-height: 38px;
    letter-spacing: normal;
    text-align: center;
  }

  &__finances-title {
    color: rgb(146, 160, 176);
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: normal;
    text-align: center;
  }

  &__last-payment-block {
    border-radius: 8px;
    box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.08);
    background: rgb(255, 255, 255);
    padding: 30px 25px 25px 25px;
    display: flex;
    flex-direction: column;
    grid-column: 2;
    grid-row: 1;
  }

  &__last-payment-header {
    color: rgb(30, 49, 71);
    font-family: Geometria, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0;
    text-align: left;
    border-bottom: 1px solid rgb(235, 239, 242);
    padding-bottom: 15px;
  }

  &__last-payment-empty-info {
    color: rgb(30, 49, 71);
    font-family: Geometria, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0;
    text-align: center;
  }

  &__last-payment-data {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding-top: 25px;
  }

  &__last-payment-row {
    display: flex;
    justify-content: space-between;
  }

  &__last-payment-title {
    color: rgb(146, 160, 176);
    font-family: Geometria, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: normal;
    text-align: left;
  }

  &__last-payment-value {
    color: rgb(30, 49, 71);
    font-family: Geometria, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: normal;
    text-align: right;
  }

  &__referrals-payout {
    grid-row: 3;
    grid-column: 1;
  }

  &__referrals-table {
    grid-row: 4;
    grid-column: 1;
  }

  &__payouts-table {
    grid-row: 5;
    grid-column: 1;
  }
}
</style>
